import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import currencyCodes from "currency-codes";
import { SERVER_BASE_URL } from "../config";
import { useNavigate } from "react-router-dom";

function SettingsPage() {
  const { user, setUser, logout } = useAuth();
  const [userName, setUserName] = useState(user?.name || "");
  const [defaultCurrency, setDefaultCurrency] = useState("USD");
  const [defaultPlace, setDefaultPlace] = useState("");
  const [defaultStartTime, setDefaultStartTime] = useState("20:00");
  const [defaultEventDuration, setDefaultEventDuration] = useState("");
  const [organization, setOrganization] = useState("");
  const [defaultAdmission, setDefaultAdmission] = useState("");
  const [timeZone, setTimeZone] = useState("UTC");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const navigate = useNavigate();

  // Список популярных часовых поясов
  const timeZones = [
    { value: "UTC", label: "UTC (Coordinated Universal Time)" },
    { value: "Europe/Moscow", label: "Moscow (UTC+3)" },
    { value: "Europe/London", label: "London (UTC+0/+1)" },
    { value: "Europe/Paris", label: "Paris (UTC+1/+2)" },
    { value: "America/New_York", label: "New York (UTC-5/-4)" },
    { value: "America/Los_Angeles", label: "Los Angeles (UTC-8/-7)" },
    { value: "Asia/Bangkok", label: "Bangkok (UTC+7)" },
    { value: "Asia/Tokyo", label: "Tokyo (UTC+9)" },
    { value: "Australia/Sydney", label: "Sydney (UTC+10/+11)" },
  ];

  useEffect(() => {
    if (user) {
      setUserName(user.name || "");
    }
  }, [user]);

  useEffect(() => {
    const fetchUserSettings = async () => {
      if (!user || !user.id) return;
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${SERVER_BASE_URL}/api/users/${user.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const userData = await response.json();
          setUserName(userData.name || "");
          setDefaultCurrency(userData.default_currency || "USD");
          setDefaultPlace(userData.default_place || "");
          setDefaultStartTime(userData.default_start_time || "20:00");
          setDefaultEventDuration(userData.default_event_duration || "");
          setOrganization(userData.organization || "");
          setDefaultAdmission(userData.default_admission || "");
          setTimeZone(userData.time_zone || "UTC");
        } else {
          console.error("Failed to load user settings");
          toast.error("Failed to load user settings");
        }
      } catch (error) {
        console.error("Error loading user settings:", error);
        toast.error("Error loading user settings");
      }
    };

    fetchUserSettings();
  }, [user]);

  const handleNameChange = (e) => setUserName(e.target.value);
  const handleCurrencyChange = (e) => setDefaultCurrency(e.target.value);
  const handlePlaceChange = (e) => setDefaultPlace(e.target.value);
  const handleStartTimeChange = (e) => setDefaultStartTime(e.target.value);
  const handleDefaultEventDurationChange = (e) => setDefaultEventDuration(e.target.value);
  const handleOrganizationChange = (e) => setOrganization(e.target.value);
  const handleAdmissionChange = (e) => setDefaultAdmission(e.target.value);
  const handleTimeZoneChange = (e) => setTimeZone(e.target.value);

  // Функция для перехода на страницу подписки
  const handleRenewSubscription = () => {
    navigate('/subscription');
  };

  // Добавляем символ для THB, SYP и т.д. 
  // Если не найдём символ в словаре, показываем сам код (например, RUB, JPY).
  const getCurrencySymbol = (code) => {
    const symbols = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      THB: "฿",
      SYP: "SYP",
      // Добавляйте нужные валюты при необходимости
    };
    return symbols[code] || code;
  };

  const handleSaveSettings = async () => {
    if (!user || !user.id) {
      toast.error("User ID is not available.");
      return;
    }
    if (!user.email.includes("@") || !user.email.includes(".")) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const updateData = {
        name: userName,
        default_currency: defaultCurrency,
        default_place: defaultPlace,
        default_start_time: defaultStartTime,
        default_event_duration: defaultEventDuration,
        organization: organization,
        default_admission: defaultAdmission,
        time_zone: timeZone,
      };

      // Для пользователей с ролью "worker" не обновляем organization
      const finalUpdateData =
      user?.role === "worker"
        ? {
            name: userName,
            default_currency: defaultCurrency,
            default_place: defaultPlace,
            default_start_time: defaultStartTime,
            default_admission: defaultAdmission,
            // time_zone не отправляем для worker
          }
    : updateData;

      const response = await fetch(`${SERVER_BASE_URL}/api/users/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(finalUpdateData),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        localStorage.setItem("defaultCurrency", updatedUser.default_currency || "");
        localStorage.setItem("defaultPlace", updatedUser.default_place || "");
        localStorage.setItem("defaultStartTime", updatedUser.default_start_time || "");
        localStorage.setItem("defaultEventDuration", updatedUser.default_event_duration || "");
        localStorage.setItem("defaultAdmission", updatedUser.default_admission || "");
        localStorage.setItem("timeZone", updatedUser.time_zone || "UTC");
        if (updatedUser.role !== "worker") {
          localStorage.setItem("organization", updatedUser.organization || "");
        }
        setUser(updatedUser);

        toast.success("Settings saved successfully!");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error saving settings");
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || !user.id) {
      toast.error("Идентификатор пользователя недоступен");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${SERVER_BASE_URL}/api/users/${user.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        toast.success("Ваш аккаунт был успешно удален");
        localStorage.removeItem("defaultCurrency");
        localStorage.removeItem("defaultPlace");
        localStorage.removeItem("defaultStartTime");
        localStorage.removeItem("defaultEventDuration");
        localStorage.removeItem("organization");
        localStorage.removeItem("defaultAdmission");
        localStorage.removeItem("timeZone");
        logout();
        navigate("/");
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Не удалось удалить аккаунт");
      }
    } catch (error) {
      console.error("Ошибка удаления аккаунта:", error);
      toast.error("Ошибка при удалении аккаунта");
    }
  };

  const DeleteConfirmationDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full">
        <h3 className="text-xl font-bold text-red-600 mb-4">Delete Account</h3>
        <p className="text-gray-700 mb-6">
          Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently lost.
        </p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => setShowDeleteConfirmation(false)}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteAccount}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Delete My Account
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {showDeleteConfirmation && <DeleteConfirmationDialog />}
      <div className="container mx-auto max-w-5xl p-4 mt-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-gray-800">User Settings</h2>
          {user?.role && (
            <div className="bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-medium">
              {user.role === "worker" ? "Employee" : "Administrator"}
            </div>
          )}
        </div>

        {/* Personal Information */}
        <div className="bg-white shadow-lg rounded-xl p-6 transition-all hover:shadow-xl">
          <div className="flex items-center justify-between border-b pb-4 mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Personal Information</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Name</p>
              <input
                type="text"
                value={userName}
                onChange={handleNameChange}
                className="w-full p-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Email</p>
              <p className="text-gray-900 font-medium">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Worker / Organizer */}
        {user?.role === "worker" ? (
          <div className="bg-white shadow-lg rounded-xl p-6">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Organization</h3>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Organization Name</p>
              <p className="text-gray-900 font-medium">{user.organization || "N/A"}</p>
            </div>
            
            <div className="mt-8">
              <button
                onClick={handleSaveSettings}
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition duration-200"
              >
                <svg
                  className="mr-2 h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Save Settings
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white shadow-lg rounded-xl p-6 mb-6 transition-all hover:shadow-xl">
            <div className="border-b pb-4 mb-6">
              <h3 className="text-xl font-semibold text-gray-800">Default Settings</h3>
              <p className="text-gray-500 mt-1">
                These parameters will be automatically applied to new events
              </p>
            </div>

            {/* 2 столбца для всех экранов */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-6">
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <div className="relative">
                  <select
                    id="currency"
                    value={defaultCurrency}
                    onChange={handleCurrencyChange}
                    className="appearance-none block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    {currencyCodes.codes().map((code) => {
                      const currency = currencyCodes.code(code);
                      return (
                        <option key={code} value={code}>
                          {code} - {currency?.currency || ""}
                        </option>
                      );
                    })}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                    <svg
                      className="h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="place" className="block text-sm font-medium text-gray-700 mb-2">
                  Venue
                </label>
                <input
                  type="text"
                  id="place"
                  value={defaultPlace}
                  onChange={handlePlaceChange}
                  className="block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter default venue"
                />
              </div>

              <div>
                <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <input
                  type="time"
                  id="startTime"
                  value={defaultStartTime}
                  onChange={handleStartTimeChange}
                  className="block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="eventDuration" className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="hidden sm:inline">Event Duration (hours)</span>
                  <span className="sm:hidden">Event Duration (h)</span>
                </label>
                <input
                  type="number"
                  id="eventDuration"
                  value={defaultEventDuration}
                  onChange={handleDefaultEventDurationChange}
                  className="block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter default event duration"
                />
              </div>

              {/* Admission Fee: символ (или код) валюты справа */}
              <div>
                <label htmlFor="admission" className="block text-sm font-medium text-gray-700 mb-2">
                  Admission Fee
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="admission"
                    value={defaultAdmission}
                    onChange={handleAdmissionChange}
                    className="block w-full pr-12 p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter default admission"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 sm:text-sm">
                    {getCurrencySymbol(defaultCurrency)}
                  </div>
                </div>
              </div>

              <div>
                <label
                  htmlFor="organization"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Organization
                </label>
                <input
                  type="text"
                  id="organization"
                  value={organization}
                  onChange={handleOrganizationChange}
                  className="block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter organization name"
                />
              </div>
              
              {/* Добавляем поле для выбора часового пояса */}
              <div>
                <label htmlFor="timeZone" className="block text-sm font-medium text-gray-700 mb-2">
                  Time Zone
                </label>
                <div className="relative">
                  <select
                    id="timeZone"
                    value={timeZone}
                    onChange={handleTimeZoneChange}
                    className="appearance-none block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    {timeZones.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                    <svg
                      className="h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <button
                onClick={handleSaveSettings}
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition duration-200"
              >
                <svg
                  className="mr-2 h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Save Settings
              </button>
            </div>
          </div>
        )}

        {user?.role !== "worker" && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 shadow-lg rounded-xl p-6 transition-all hover:shadow-xl">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-semibold text-gray-800">
                  Subscription Management
                </h3>
                <p className="text-gray-600 mt-1">
                  Manage your account subscription
                </p>
              </div>
              <button
                onClick={handleRenewSubscription}
                className="inline-flex items-center px-5 py-2.5 border border-blue-600 text-base font-medium rounded-lg text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200"
              >
                Renew Subscription
              </button>
            </div>
          </div>
        )}

        {/* Delete Account Section */}
        <div className="bg-white shadow-lg rounded-xl p-6  border border-red-100">
          <div className="border-b border-red-100 pb-4 mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Delete Account</h3>
            <p className="text-gray-500 mt-1">
              Permanently delete your account and all associated data
            </p>
          </div>

          <div className="flex flex-col">
            <p className="text-gray-700 mb-4">
              Once you delete your account, there is no going back. All your data will be permanently removed.
            </p>
            <button
              onClick={() => setShowDeleteConfirmation(true)}
              className="self-start px-5 py-2.5 bg-white border border-red-500 text-red-600 font-medium rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-200"
            >
              Delete My Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;